import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../../components/SEO';
import { blogPosts } from '../../mocks/blogPosts';

const DOMAIN = 'https://alliancesecuritysystems.ca';

interface SitemapSection {
  title: string;
  icon: string;
  color: string;
  links: { label: string; path: string; priority?: string }[];
}

const sitemapSections: SitemapSection[] = [
  {
    title: 'Core Pages',
    icon: 'ri-home-4-line',
    color: 'text-[#D4A853]',
    links: [
      { label: 'Home', path: '/', priority: 'High' },
      { label: 'Contact Us', path: '/contact', priority: 'High' },
      { label: 'FAQ', path: '/faq', priority: 'Medium' },
      { label: 'Our Clients', path: '/clients', priority: 'Medium' },
      { label: 'Privacy Policy', path: '/privacy-policy', priority: 'Low' },
    ],
  },
  {
    title: 'Security Services',
    icon: 'ri-shield-check-line',
    color: 'text-[#D4A853]',
    links: [
      { label: 'Alarm Packages', path: '/alarm-packages', priority: 'High' },
      { label: 'Camera Packages', path: '/camera-packages', priority: 'High' },
      { label: 'Access Control', path: '/access-control', priority: 'High' },
      { label: 'Live Monitoring', path: '/live-monitoring', priority: 'High' },
      { label: 'Business Security', path: '/business-security', priority: 'High' },
      { label: 'Commercial Security', path: '/commercial-security', priority: 'High' },
    ],
  },
  {
    title: 'Service Areas',
    icon: 'ri-map-pin-2-line',
    color: 'text-[#D4A853]',
    links: [
      { label: 'All Service Areas', path: '/service-areas', priority: 'High' },
      { label: 'Toronto', path: '/service-areas/toronto', priority: 'High' },
      { label: 'Mississauga', path: '/service-areas/mississauga', priority: 'High' },
      { label: 'Brampton', path: '/service-areas/brampton', priority: 'High' },
      { label: 'Oakville', path: '/service-areas/oakville', priority: 'Medium' },
      { label: 'Hamilton', path: '/service-areas/hamilton', priority: 'Medium' },
      { label: 'Richmond Hill', path: '/service-areas/richmond-hill', priority: 'Medium' },
      { label: 'Vaughan', path: '/service-areas/vaughan', priority: 'Medium' },
      { label: 'Whitby', path: '/service-areas/whitby', priority: 'Medium' },
      { label: 'Ajax', path: '/service-areas/ajax', priority: 'Medium' },
      { label: 'Oshawa', path: '/service-areas/oshawa', priority: 'Medium' },
    ],
  },
  {
    title: 'Commercial Security by City',
    icon: 'ri-building-4-line',
    color: 'text-[#D4A853]',
    links: [
      { label: 'Commercial Security Hub', path: '/commercial-security', priority: 'High' },
      { label: 'Commercial — Service Areas', path: '/commercial-security/service-areas', priority: 'Medium' },
      { label: 'Commercial — Toronto', path: '/commercial-security/toronto', priority: 'Medium' },
      { label: 'Commercial — Mississauga', path: '/commercial-security/mississauga', priority: 'Medium' },
      { label: 'Commercial — Brampton', path: '/commercial-security/brampton', priority: 'Medium' },
      { label: 'Commercial — Oakville', path: '/commercial-security/oakville', priority: 'Medium' },
      { label: 'Commercial — Hamilton', path: '/commercial-security/hamilton', priority: 'Medium' },
      { label: 'Commercial — Richmond Hill', path: '/commercial-security/richmond-hill', priority: 'Medium' },
      { label: 'Commercial — Vaughan', path: '/commercial-security/vaughan', priority: 'Medium' },
      { label: 'Commercial — Whitby', path: '/commercial-security/whitby', priority: 'Medium' },
      { label: 'Commercial — Ajax', path: '/commercial-security/ajax', priority: 'Medium' },
      { label: 'Commercial — Oshawa', path: '/commercial-security/oshawa', priority: 'Medium' },
    ],
  },
];

const priorityBadge: Record<string, string> = {
  High: 'bg-emerald-100 text-emerald-700',
  Medium: 'bg-amber-100 text-amber-700',
  Low: 'bg-gray-100 text-gray-500',
};

export default function SitemapPage() {
  const [activeFilter, setActiveFilter] = useState<'all' | 'service-areas' | 'blog' | 'commercial'>('all');

  const totalLinks =
    sitemapSections.reduce((acc, s) => acc + s.links.length, 0) + blogPosts.length;

  const sitemapStructuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://alliancesecuritysystems.com/sitemap#webpage",
      "name": "Sitemap — Alliance Security Systems",
      "url": "https://alliancesecuritysystems.com/sitemap",
      "description": "Complete sitemap for Alliance Security Systems. Browse all service area pages, blog posts, commercial security city pages, and core service pages across the GTA.",
      "isPartOf": { "@id": "https://alliancesecuritysystems.com/#website" }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": "https://alliancesecuritysystems.com/sitemap#breadcrumb",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://alliancesecuritysystems.com/" },
        { "@type": "ListItem", "position": 2, "name": "Sitemap", "item": "https://alliancesecuritysystems.com/sitemap" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Sitemap — Alliance Security Systems | All Pages & Service Areas"
        description="Complete sitemap for Alliance Security Systems. Browse all service area pages, blog posts, commercial security city pages, and core service pages across the GTA."
        keywords="Alliance Security sitemap, security service areas GTA, security blog posts, commercial security pages"
        canonical="/sitemap"
        structuredData={sitemapStructuredData}
      />

      {/* Nav */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-4 py-2.5 bg-[#0A1628]">
              <span className="text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
              <span className="text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="text-sm font-medium text-gray-600 hover:text-[#D4A853] transition-colors whitespace-nowrap">Blog</Link>
            <Link to="/service-areas" className="text-sm font-medium text-gray-600 hover:text-[#D4A853] transition-colors whitespace-nowrap">Service Areas</Link>
            <Link to="/contact" className="bg-[#D4A853] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all whitespace-nowrap cursor-pointer">Get Free Quote</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-[#0A1628] py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '36px 36px' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="text-white/60 hover:text-[#D4A853] transition-colors" itemProp="item">
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li className="text-white/30"><i className="ri-arrow-right-s-line"></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span className="text-[#D4A853] font-semibold" itemProp="name">Sitemap</span>
                <meta itemProp="position" content="2" />
              </li>
            </ol>
          </nav>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <span className="inline-block bg-[#D4A853] text-white px-5 py-1.5 rounded-full text-xs font-semibold mb-4 tracking-widest">SITEMAP</span>
              <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                Complete Site Directory
              </h1>
              <p className="text-white/70 text-lg max-w-2xl">
                Every page on the Alliance Security Systems website — service areas, blog posts, commercial security city pages, and more.
              </p>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="bg-white/10 border border-white/20 rounded-2xl px-6 py-4 text-center">
                <p className="text-3xl font-extrabold text-[#D4A853]">{totalLinks}</p>
                <p className="text-white/60 text-xs mt-1">Total Pages</p>
              </div>
              <a
                href="/sitemap.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#D4A853] text-white px-5 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-code-line text-base"></i>
                XML Sitemap
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <div className="bg-white border-b border-gray-100 sticky top-[73px] z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {[
              { key: 'all', label: 'All Pages', icon: 'ri-layout-grid-line' },
              { key: 'service-areas', label: 'Service Areas', icon: 'ri-map-pin-2-line' },
              { key: 'commercial', label: 'Commercial', icon: 'ri-building-4-line' },
              { key: 'blog', label: 'Blog Posts', icon: 'ri-article-line' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key as typeof activeFilter)}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  activeFilter === tab.key
                    ? 'bg-[#0A1628] text-white'
                    : 'text-gray-500 hover:text-[#0A1628] hover:bg-gray-100'
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-14">

        {/* Core + Services + Service Areas + Commercial */}
        {(activeFilter === 'all' || activeFilter === 'service-areas' || activeFilter === 'commercial') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-14">
            {sitemapSections
              .filter((section) => {
                if (activeFilter === 'service-areas') return section.title === 'Service Areas';
                if (activeFilter === 'commercial') return section.title === 'Commercial Security by City';
                return true;
              })
              .map((section) => (
                <div key={section.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  {/* Section Header */}
                  <div className="bg-[#F8F9FA] px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#D4A853]/15 rounded-xl flex items-center justify-center">
                      <i className={`${section.icon} text-lg ${section.color}`}></i>
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-[#0A1628]">{section.title}</h2>
                      <p className="text-xs text-gray-400">{section.links.length} pages</p>
                    </div>
                  </div>

                  {/* Links */}
                  <ul className="divide-y divide-gray-50">
                    {section.links.map((link) => (
                      <li key={link.path}>
                        <Link
                          to={link.path}
                          className="flex items-center justify-between px-6 py-3.5 hover:bg-[#D4A853]/5 transition-colors group cursor-pointer"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <i className="ri-link text-gray-300 group-hover:text-[#D4A853] text-sm flex-shrink-0 transition-colors"></i>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-[#0A1628] group-hover:text-[#D4A853] transition-colors truncate">{link.label}</p>
                              <p className="text-xs text-gray-400 truncate">{DOMAIN}{link.path}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                            {link.priority && (
                              <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${priorityBadge[link.priority]}`}>
                                {link.priority}
                              </span>
                            )}
                            <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#D4A853] transition-colors"></i>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        )}

        {/* Blog Posts Section */}
        {(activeFilter === 'all' || activeFilter === 'blog') && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            {/* Header */}
            <div className="bg-[#F8F9FA] px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#D4A853]/15 rounded-xl flex items-center justify-center">
                  <i className="ri-article-line text-lg text-[#D4A853]"></i>
                </div>
                <div>
                  <h2 className="text-base font-bold text-[#0A1628]">Blog Posts</h2>
                  <p className="text-xs text-gray-400">{blogPosts.length} articles</p>
                </div>
              </div>
              <Link
                to="/blog"
                className="text-xs font-semibold text-[#D4A853] hover:text-[#B8923D] transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                View All <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>

            {/* Blog Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-50">
              {blogPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="flex items-start gap-3 px-5 py-4 hover:bg-[#D4A853]/5 transition-colors group cursor-pointer border-b border-gray-50"
                >
                  <i className="ri-article-line text-gray-300 group-hover:text-[#D4A853] text-sm flex-shrink-0 mt-0.5 transition-colors"></i>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#0A1628] group-hover:text-[#D4A853] transition-colors line-clamp-2 leading-snug mb-1">
                      {post.title}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {post.category}
                      </span>
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">
                        <i className="ri-time-line mr-0.5"></i>{post.readTime} min
                      </span>
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">{post.date}</span>
                    </div>
                    <p className="text-[10px] text-gray-300 mt-1 truncate">{DOMAIN}/blog/{post.slug}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* XML Sitemap CTA */}
        <div className="mt-12 bg-gradient-to-r from-[#0A1628] to-[#1a2d4a] rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">XML Sitemap for Search Engines</h3>
            <p className="text-white/60 text-sm">
              Submit <strong className="text-white/80">{DOMAIN}/sitemap.xml</strong> to Google Search Console and Bing Webmaster Tools to accelerate indexing.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <a
              href="/sitemap.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-code-line"></i>
              View XML Sitemap
            </a>
            <a
              href="https://search.google.com/search-console"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-2 border border-white/30 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-google-line"></i>
              Search Console
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-10 mx-3 rounded-t-3xl mt-4">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-4 py-2.5 bg-[#0A1628]">
            <span className="text-xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
            <span className="text-[9px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
          </Link>
          <p className="text-white/40 text-xs text-center">© 2025 Alliance Security Systems. All rights reserved.</p>
          <div className="flex items-center gap-4 text-xs text-white/40">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <a href="/sitemap.xml" className="hover:text-white transition-colors">XML Sitemap</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
